'use client';

import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, X, Loader2, MapPin, Camera, ChevronRight, ChevronLeft, Navigation, CheckCircle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { OwnerListing } from '@/lib/ownerListings';
import { photoService } from '@/lib/services/dataService';
import { useAuth } from '@/contexts/AuthContext';

interface AddListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (listing: OwnerListing) => void;
  editListing: OwnerListing | null;
}

interface ListingFormData {
  title: string;
  houseType: string;
  bhk: string;
  bedrooms: number;
  bathrooms: number;
  builtUpArea: number;
  furnishing: string;
  parking: boolean;
  waterFacility: boolean;
  powerBackup: boolean;
  lift: boolean;
  wifi: boolean;
  floorNumber: number;
  facing: string;
  availableFrom: string;
  monthlyRent: number;
  securityDeposit: number;
  maintenanceCharges: number;
  negotiable: boolean;
  address: string;
  locality: string;
  city: string;
  state: string;
  pincode: string;
  landmarks: string;
  googleMapsLink: string;
}

const STEPS = ['Photos', 'Specifications', 'Rent Details', 'Location'];

const HOUSE_TYPES = ['Apartment', 'Independent House', 'Villa', 'Room', 'Commercial Room', 'Commercial Office', 'Studio'];
const BHK_OPTIONS = ['1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '4+ BHK'];
const FURNISHING_OPTIONS = ['Fully Furnished', 'Semi Furnished', 'Unfurnished'];
const FACING_OPTIONS = ['East', 'West', 'North', 'South', 'North-East', 'North-West', 'South-East', 'South-West'];
const INDIA_STATES = [
  'Andhra Pradesh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Punjab', 'Rajasthan',
  'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'West Bengal',
];

const PLACEHOLDER_PHOTOS = [
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&q=80',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&q=80',
  'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&q=80',
];

export default function AddListingModal({ isOpen, onClose, onSubmit, editListing }: AddListingModalProps) {
  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState<string[]>(editListing?.photos || []);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ListingFormData>({
    defaultValues: editListing
      ? {
          title: editListing.title,
          houseType: editListing.houseType,
          bhk: editListing.bhk,
          bedrooms: editListing.bedrooms,
          bathrooms: editListing.bathrooms,
          builtUpArea: editListing.builtUpArea,
          furnishing: editListing.furnishing,
          parking: editListing.parking,
          waterFacility: editListing.waterFacility,
          powerBackup: editListing.powerBackup,
          lift: editListing.lift,
          wifi: editListing.wifi,
          floorNumber: editListing.floorNumber,
          facing: editListing.facing,
          availableFrom: editListing.availableFromRaw,
          monthlyRent: editListing.monthlyRent,
          securityDeposit: editListing.securityDeposit,
          maintenanceCharges: editListing.maintenanceCharges,
          negotiable: editListing.negotiable,
          address: editListing.address,
          locality: editListing.locality,
          city: editListing.city,
          state: editListing.state,
          pincode: editListing.pincode,
          landmarks: editListing.landmarks,
          googleMapsLink: editListing.googleMapsLink,
        }
      : {
          parking: false,
          waterFacility: true,
          powerBackup: false,
          lift: false,
          wifi: false,
          negotiable: false,
          maintenanceCharges: 0,
        },
  });

  const handleClose = () => {
    reset();
    setStep(0);
    setPhotos([]);
    setPhotoFiles([]);
    setUploadProgress('');
    setLocationError('');
    setSubmitted(false);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newFiles = Array.from(files).slice(0, 10 - photos.length);
    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
    setPhotoFiles((prev) => [...prev, ...newFiles].slice(0, 10));
    setPhotos((prev) => [...prev, ...newPreviews].slice(0, 10));
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Auto-fetch location and generate Google Maps link
  const handleAutoLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const mapsLink = `https://www.google.com/maps?q=${latitude},${longitude}`;
        setValue('googleMapsLink', mapsLink);
        setLocationLoading(false);
        setLocationError('');
      },
      (error) => {
        setLocationLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError('Location permission denied. Please allow location access and try again.');
        } else {
          setLocationError('Unable to get your location. Please enter the link manually.');
        }
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const stepFields: Record<number, (keyof ListingFormData)[]> = {
    0: [],
    1: ['title', 'houseType', 'bhk', 'bedrooms', 'bathrooms', 'builtUpArea', 'furnishing', 'floorNumber', 'facing', 'availableFrom'],
    2: ['monthlyRent', 'securityDeposit'],
    3: ['address', 'locality', 'city', 'state', 'pincode'],
  };

  const handleNext = async () => {
    const fields = stepFields[step];
    const valid = fields.length === 0 ? true : await trigger(fields);
    if (step === 0 && photos.length === 0) return;
    if (valid) setStep((s) => Math.min(3, s + 1));
  };

  const onFormSubmit = async (data: ListingFormData) => {
    setIsSubmitting(true);
    try {
      let uploadedPhotoUrls: string[] = [];
      const userId = user?.id || 'anonymous';

      if (photoFiles.length > 0) {
        setUploadProgress(`Uploading ${photoFiles.length} photo(s)...`);
        uploadedPhotoUrls = await photoService.uploadPhotos(photoFiles, userId);
      }

      const existingUrls = photos.filter((p) => p.startsWith('http') && !p.startsWith('blob:'));
      const finalPhotos = [...uploadedPhotoUrls, ...existingUrls].slice(0, 10);

      setUploadProgress('Saving listing...');

      const newListing: OwnerListing = {
        id: `listing-${Date.now()}`,
        title: data.title,
        houseType: data.houseType,
        bhk: data.bhk,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        builtUpArea: data.builtUpArea,
        furnishing: data.furnishing,
        parking: data.parking,
        waterFacility: data.waterFacility,
        powerBackup: data.powerBackup,
        lift: data.lift,
        wifi: data.wifi,
        floorNumber: data.floorNumber,
        facing: data.facing,
        availableFrom: new Date(data.availableFrom).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
        }),
        availableFromRaw: data.availableFrom,
        monthlyRent: data.monthlyRent,
        securityDeposit: data.securityDeposit,
        maintenanceCharges: data.maintenanceCharges,
        negotiable: data.negotiable,
        address: data.address,
        locality: data.locality,
        city: data.city,
        state: data.state,
        pincode: data.pincode,
        landmarks: data.landmarks,
        googleMapsLink: data.googleMapsLink,
        photos: finalPhotos.length > 0 ? finalPhotos : [PLACEHOLDER_PHOTOS[0]],
        coverPhoto: finalPhotos[0] || PLACEHOLDER_PHOTOS[0],
        status: 'Pending Review',
        views: 0,
        inquiries: 0,
        listedOn: new Date().toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
        }),
        verified: false,
        featured: false,
        ownerName: user?.user_metadata?.full_name || 'Property Owner',
        ownerPhone: user?.user_metadata?.phone || '',
        ownerEmail: user?.email || '',
      };

      // Submit listing — free, no payment required
      await onSubmit(newListing);
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  // Success screen
  if (submitted) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Listing Submitted" size="xl">
        <div className="p-10 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h3 className="text-xl font-extrabold text-foreground mb-2">Listing Submitted for Review!</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs">
            Your property has been submitted for free. Our team will review and approve it shortly.
          </p>
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700 font-semibold mb-6">
            <CheckCircle size={15} />
            Listing is free — no charges apply
          </div>
          <button onClick={handleClose} className="btn-primary justify-center px-8">
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editListing ? 'Edit Listing' : 'Add New Listing (Free)'}
      size="xl"
    >
      {/* Step Progress */}
      <div className="px-6 pt-5 pb-4 border-b border-border">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={`step-${s}`}>
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-200 ${
                    i < step
                      ? 'bg-primary border-primary text-primary-foreground'
                      : i === step
                      ? 'border-primary text-primary bg-secondary' :'border-border text-muted-foreground bg-muted'
                  }`}
                >
                  {i < step ? '✓' : i + 1}
                </div>
                <span className={`text-xs font-semibold hidden sm:block ${i === step ? 'text-primary' : 'text-muted-foreground'}`}>
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 mb-4 transition-all duration-300 ${i < step ? 'bg-primary' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        {/* Free badge */}
        <div className="flex items-center justify-center mt-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">
            <CheckCircle size={11} />
            Listing is FREE — No charges
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="p-6">
          {/* Step 0: Photos */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-bold text-foreground mb-1">Upload Property Photos</h3>
                <p className="helper-text">Add up to 10 photos. First photo will be the cover image. Clear photos get 3× more inquiries.</p>
              </div>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:border-primary hover:bg-secondary/20 transition-all duration-150 group"
              >
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/10 transition-colors">
                  <Camera size={22} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="text-sm font-bold text-foreground mb-1">Click to upload photos</div>
                <div className="text-xs text-muted-foreground">JPG, PNG up to 5MB each · Max 10 photos</div>
                <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={handleFileChange} aria-label="Upload property photos" />
              </div>
              {photos.length === 0 && <p className="text-xs text-amber-600 font-medium">⚠ At least one photo is required to proceed</p>}
              {photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {photos.map((photo, i) => (
                    <div key={`photo-preview-${i + 1}`} className="relative group rounded-xl overflow-hidden aspect-square bg-muted">
                      <img src={photo} alt={`Property photo ${i + 1}`} className="w-full h-full object-cover" />
                      {i === 0 && <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-white text-xs font-bold text-center py-1">Cover</div>}
                      <button type="button" onClick={() => removePhoto(i)} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600" aria-label={`Remove photo ${i + 1}`}><X size={12} /></button>
                    </div>
                  ))}
                  {photos.length < 10 && (
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-secondary/20 transition-all text-muted-foreground hover:text-primary" aria-label="Add more photos">
                      <Upload size={18} /><span className="text-xs font-semibold">Add More</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 1: Specifications */}
          {step === 1 && (
            <div className="space-y-5">
              <h3 className="text-base font-bold text-foreground">House Specifications</h3>
              <div>
                <label htmlFor="listing-title" className="label-text">Listing Title</label>
                <p className="helper-text">A clear, descriptive title gets more views</p>
                <input id="listing-title" type="text" placeholder="e.g. Spacious 2BHK Apartment near Indiranagar Metro" className={`input-field mt-1.5 ${errors.title ? 'border-red-400' : ''}`} {...register('title', { required: 'Listing title is required', minLength: { value: 10, message: 'Title must be at least 10 characters' } })} />
                {errors.title && <p className="error-text" role="alert">{errors.title.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="house-type" className="label-text">Property Type</label>
                  <select id="house-type" className={`input-field mt-1 ${errors.houseType ? 'border-red-400' : ''}`} {...register('houseType', { required: 'Property type is required' })}>
                    <option value="">Select type</option>
                    {HOUSE_TYPES.map((t) => <option key={`htype-${t}`} value={t}>{t}</option>)}
                  </select>
                  {errors.houseType && <p className="error-text" role="alert">{errors.houseType.message}</p>}
                </div>
                <div>
                  <label htmlFor="bhk-type" className="label-text">BHK Type</label>
                  <select id="bhk-type" className={`input-field mt-1 ${errors.bhk ? 'border-red-400' : ''}`} {...register('bhk', { required: 'BHK type is required' })}>
                    <option value="">Select BHK</option>
                    {BHK_OPTIONS.map((b) => <option key={`bhk-opt-${b}`} value={b}>{b}</option>)}
                  </select>
                  {errors.bhk && <p className="error-text" role="alert">{errors.bhk.message}</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="bedrooms" className="label-text">Bedrooms</label>
                  <input id="bedrooms" type="number" min={0} max={20} placeholder="2" className={`input-field mt-1 ${errors.bedrooms ? 'border-red-400' : ''}`} {...register('bedrooms', { required: 'Required', min: { value: 0, message: 'Min 0' }, valueAsNumber: true })} />
                  {errors.bedrooms && <p className="error-text" role="alert">{errors.bedrooms.message}</p>}
                </div>
                <div>
                  <label htmlFor="bathrooms" className="label-text">Bathrooms</label>
                  <input id="bathrooms" type="number" min={1} max={10} placeholder="2" className={`input-field mt-1 ${errors.bathrooms ? 'border-red-400' : ''}`} {...register('bathrooms', { required: 'Required', min: { value: 1, message: 'Min 1' }, valueAsNumber: true })} />
                  {errors.bathrooms && <p className="error-text" role="alert">{errors.bathrooms.message}</p>}
                </div>
                <div>
                  <label htmlFor="built-up-area" className="label-text">Area (sq.ft)</label>
                  <input id="built-up-area" type="number" min={100} placeholder="1050" className={`input-field mt-1 ${errors.builtUpArea ? 'border-red-400' : ''}`} {...register('builtUpArea', { required: 'Area is required', min: { value: 100, message: 'Min 100 sq.ft' }, valueAsNumber: true })} />
                  {errors.builtUpArea && <p className="error-text" role="alert">{errors.builtUpArea.message}</p>}
                </div>
              </div>
              <div>
                <label className="label-text">Furnishing Status</label>
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  {FURNISHING_OPTIONS.map((f) => {
                    const current = watch('furnishing');
                    return (
                      <label key={`furn-opt-${f}`} className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 cursor-pointer text-sm font-semibold transition-all duration-150 ${current === f ? 'border-primary bg-secondary text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                        <input type="radio" value={f} className="sr-only" {...register('furnishing', { required: 'Furnishing status is required' })} />
                        {f}
                      </label>
                    );
                  })}
                </div>
                {errors.furnishing && <p className="error-text" role="alert">{errors.furnishing.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="floor-number" className="label-text">Floor Number</label>
                  <input id="floor-number" type="number" min={0} max={50} placeholder="3" className={`input-field mt-1 ${errors.floorNumber ? 'border-red-400' : ''}`} {...register('floorNumber', { required: 'Floor number is required', min: { value: 0, message: 'Ground floor = 0' }, valueAsNumber: true })} />
                  {errors.floorNumber && <p className="error-text" role="alert">{errors.floorNumber.message}</p>}
                </div>
                <div>
                  <label htmlFor="facing" className="label-text">Facing Direction</label>
                  <select id="facing" className={`input-field mt-1 ${errors.facing ? 'border-red-400' : ''}`} {...register('facing', { required: 'Facing direction is required' })}>
                    <option value="">Select direction</option>
                    {FACING_OPTIONS.map((f) => <option key={`facing-${f}`} value={f}>{f}</option>)}
                  </select>
                  {errors.facing && <p className="error-text" role="alert">{errors.facing.message}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="available-from" className="label-text">Available From</label>
                <input id="available-from" type="date" className={`input-field mt-1 ${errors.availableFrom ? 'border-red-400' : ''}`} {...register('availableFrom', { required: 'Availability date is required' })} />
                {errors.availableFrom && <p className="error-text" role="alert">{errors.availableFrom.message}</p>}
              </div>
              <div>
                <label className="label-text">Amenities & Features</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                  {[{ name: 'parking' as const, label: 'Parking' }, { name: 'waterFacility' as const, label: '24hr Water' }, { name: 'powerBackup' as const, label: 'Power Backup' }, { name: 'lift' as const, label: 'Lift / Elevator' }, { name: 'wifi' as const, label: 'WiFi Ready' }].map((amenity) => (
                    <label key={`amenity-check-${amenity.name}`} className="flex items-center gap-2.5 p-3 rounded-xl border border-border hover:border-primary/40 cursor-pointer transition-colors group">
                      <input type="checkbox" className="w-4 h-4 accent-primary rounded" {...register(amenity.name)} />
                      <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{amenity.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Rent Details */}
          {step === 2 && (
            <div className="space-y-5">
              <h3 className="text-base font-bold text-foreground">Rent & Deposit Details</h3>
              <div>
                <label htmlFor="monthly-rent" className="label-text">Monthly Rent (₹)</label>
                <p className="helper-text">Set a competitive price based on your locality</p>
                <div className="relative mt-1.5">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">₹</span>
                  <input id="monthly-rent" type="number" min={1000} placeholder="25000" className={`input-field pl-8 ${errors.monthlyRent ? 'border-red-400' : ''}`} {...register('monthlyRent', { required: 'Monthly rent is required', min: { value: 1000, message: 'Minimum rent is ₹1,000' }, valueAsNumber: true })} />
                </div>
                {errors.monthlyRent && <p className="error-text" role="alert">{errors.monthlyRent.message}</p>}
              </div>
              <div>
                <label htmlFor="security-deposit" className="label-text">Security Deposit (₹)</label>
                <p className="helper-text">Typically 1–3 months rent. Refundable at lease end.</p>
                <div className="relative mt-1.5">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">₹</span>
                  <input id="security-deposit" type="number" min={0} placeholder="50000" className={`input-field pl-8 ${errors.securityDeposit ? 'border-red-400' : ''}`} {...register('securityDeposit', { required: 'Security deposit is required', min: { value: 0, message: 'Cannot be negative' }, valueAsNumber: true })} />
                </div>
                {errors.securityDeposit && <p className="error-text" role="alert">{errors.securityDeposit.message}</p>}
              </div>
              <div>
                <label htmlFor="maintenance" className="label-text">Monthly Maintenance Charges (₹)</label>
                <p className="helper-text">Enter 0 if maintenance is included in rent</p>
                <div className="relative mt-1.5">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">₹</span>
                  <input id="maintenance" type="number" min={0} placeholder="1500" className="input-field pl-8" {...register('maintenanceCharges', { min: { value: 0, message: 'Cannot be negative' }, valueAsNumber: true })} />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/40 cursor-pointer transition-colors group">
                  <input type="checkbox" className="w-5 h-5 accent-primary rounded" {...register('negotiable')} />
                  <div>
                    <div className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">Rent is Negotiable</div>
                    <div className="text-xs text-muted-foreground">This will show a &quot;Negotiable&quot; tag on your listing — attracts more inquiries</div>
                  </div>
                </label>
              </div>
              {watch('monthlyRent') > 0 && (
                <div className="p-4 bg-secondary/40 rounded-xl border border-border">
                  <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Rent Summary Preview</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Monthly Rent</div>
                      <div className="text-base font-extrabold text-primary tabular-nums">₹{(watch('monthlyRent') || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Security Deposit</div>
                      <div className="text-base font-bold text-foreground tabular-nums">₹{(watch('securityDeposit') || 0).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Location */}
          {step === 3 && (
            <div className="space-y-5">
              <h3 className="text-base font-bold text-foreground">Property Location</h3>
              <div>
                <label htmlFor="address" className="label-text">Full Address</label>
                <p className="helper-text">Door no., building name, street name</p>
                <textarea id="address" rows={2} placeholder="Flat 4B, Prestige Lakeside Habitat, Whitefield Main Road" className={`input-field mt-1.5 resize-none ${errors.address ? 'border-red-400' : ''}`} {...register('address', { required: 'Full address is required', minLength: { value: 10, message: 'Please enter a complete address' } })} />
                {errors.address && <p className="error-text" role="alert">{errors.address.message}</p>}
              </div>
              <div>
                <label htmlFor="locality" className="label-text">Locality / Area</label>
                <input id="locality" type="text" placeholder="Whitefield, Koramangala, Bandra West..." className={`input-field ${errors.locality ? 'border-red-400' : ''}`} {...register('locality', { required: 'Locality is required' })} />
                {errors.locality && <p className="error-text" role="alert">{errors.locality.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="label-text">City</label>
                  <input id="city" type="text" placeholder="Bangalore" className={`input-field ${errors.city ? 'border-red-400' : ''}`} {...register('city', { required: 'City is required' })} />
                  {errors.city && <p className="error-text" role="alert">{errors.city.message}</p>}
                </div>
                <div>
                  <label htmlFor="state" className="label-text">State</label>
                  <select id="state" className={`input-field ${errors.state ? 'border-red-400' : ''}`} {...register('state', { required: 'State is required' })}>
                    <option value="">Select state</option>
                    {INDIA_STATES.map((s) => <option key={`state-${s}`} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <p className="error-text" role="alert">{errors.state.message}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="pincode" className="label-text">Pincode</label>
                <input id="pincode" type="text" placeholder="560066" maxLength={6} className={`input-field ${errors.pincode ? 'border-red-400' : ''}`} {...register('pincode', { required: 'Pincode is required', pattern: { value: /^\d{6}$/, message: 'Enter a valid 6-digit pincode' } })} />
                {errors.pincode && <p className="error-text" role="alert">{errors.pincode.message}</p>}
              </div>
              <div>
                <label htmlFor="landmarks" className="label-text">Nearby Landmarks</label>
                <p className="helper-text">Helps tenants find the property easily</p>
                <input id="landmarks" type="text" placeholder="500m from Whitefield Metro, 1km from Forum Mall" className="input-field mt-1.5" {...register('landmarks')} />
              </div>
              <div>
                <label htmlFor="maps-link" className="label-text">
                  <MapPin size={14} className="inline mr-1 text-primary" />
                  Google Maps Link
                </label>
                <p className="helper-text">Paste the Google Maps share link or use your current location</p>
                <div className="flex gap-2 mt-1.5">
                  <input id="maps-link" type="url" placeholder="https://maps.google.com/..." className="input-field flex-1" {...register('googleMapsLink')} />
                  <button type="button" onClick={handleAutoLocation} disabled={locationLoading} title="Use my current location" className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 text-xs font-bold transition-colors disabled:opacity-60 shrink-0">
                    {locationLoading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
                    {locationLoading ? 'Locating...' : 'Use Location'}
                  </button>
                </div>
                {locationError && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><span>⚠</span> {locationError}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
          <button type="button" onClick={() => step > 0 ? setStep((s) => s - 1) : handleClose()} className="btn-secondary">
            <ChevronLeft size={16} />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</div>
          {step < 3 ? (
            <button type="button" onClick={handleNext} className="btn-primary">
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <><Loader2 size={16} className="animate-spin" />{uploadProgress || 'Saving...'}</>
              ) : (
                <><CheckCircle size={16} />{editListing ? 'Save Changes' : 'Submit Listing (Free)'}</>
              )}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}